import { Badge, Button, type TableColumn } from "../../ds";
import type {
  FirewallForwarding,
  FirewallRedirect,
  FirewallRule,
  FirewallState,
  FirewallZone,
  LanClient,
} from "../../lib/api";
import type { FieldOption, FieldSpec } from "./EntryModal";

// Was die Firewall-Sektion bearbeiten kann, an EINER Stelle beschrieben:
// Spalten für die Tabelle, Felder für das Formular, Leerzeile für „neu“.
// Vier Listen mit vier handgeschriebenen Formularen wären viermal dieselbe
// Arbeit — und drei Gelegenheiten, eine davon zu vergessen.

export type FirewallEntry = FirewallZone | FirewallForwarding | FirewallRedirect | FirewallRule;

/** Eine der vier uci-Listen von `firewall_get`. */
export type CollectionKey = "zones" | "forwardings" | "redirects" | "rules";

/** Reihenfolge in der Oberfläche: erst, was man am häufigsten anfasst. */
export const COLLECTION_KEYS: CollectionKey[] = ["redirects", "rules", "zones", "forwardings"];

export interface CollectionSpec<T extends FirewallEntry> {
  key: CollectionKey;
  title: string;
  singular: string;
  description: string;
  blank: T;
  fields: FieldSpec[];
  columns: TableColumn<T>[];
  empty: string;
}

/**
 * Woraus die Auswahlfelder ihre Einträge nehmen.
 *
 * Zonen und Geräte stehen nicht in der Spec, sondern im Gerät: die Zonen im
 * Entwurf daneben, die Geräte in `lan_clients`. Von Hand getippte Zonennamen
 * waren der häufigste Grund, warum eine Freigabe nach dem Anwenden nichts tat.
 */
export interface FirewallChoices {
  /** Zonennamen aus dem Entwurf — nicht vom Router, damit eine gerade
   *  angelegte Zone sofort wählbar ist. */
  zones: string[];
  /** ARP/DHCP-Nachbarn als Vorschläge für die Ziel-IP. */
  clients: FieldOption[];
  /** Die Geräteliste ist noch unterwegs — sagt es im Dialog, statt eine leere
   *  Auswahl zu zeigen. */
  clientsLoading: boolean;
}

/** `10.0.0.5 · wallbox-hof` — die Adresse zuerst, sie ist der Wert. */
export function clientOptions(clients: LanClient[]): FieldOption[] {
  return clients
    .filter((c) => c.ip)
    .map((c) => ({ value: c.ip, label: c.hostname ? `${c.ip} · ${c.hostname}` : c.ip }));
}

const POLICIES = ["ACCEPT", "REJECT", "DROP"];
const PROTOCOLS = ["tcp", "udp", "tcp udp", "icmp", "all"];
const FAMILIES = ["any", "ipv4", "ipv6"];

const TARGET_VARIANT: Record<string, "success" | "danger" | "warn" | "neutral"> = {
  ACCEPT: "success",
  REJECT: "danger",
  DROP: "danger",
  DNAT: "warn",
  SNAT: "warn",
  MARK: "warn",
};

const targetBadge = (target: string) => (
  <Badge variant={TARGET_VARIANT[target] ?? "neutral"}>{target}</Badge>
);

const activeBadge = (on: boolean) =>
  on ? (
    <Badge variant="success" dot>
      an
    </Badge>
  ) : (
    <Badge variant="neutral">aus</Badge>
  );

/** Bearbeiten/Löschen — für jede der vier Tabellen dieselbe letzte Spalte. */
function actionColumn<T extends FirewallEntry>(
  onEdit: (entry: T, index: number) => void,
  onDelete: (index: number) => void,
): TableColumn<T> {
  return {
    key: "edit",
    header: "",
    align: "right",
    className: "actions",
    cell: (entry, index) => (
      <div className="flex justify-end gap-1">
        <Button size="sm" variant="ghost" iconName="edit" onClick={() => onEdit(entry, index)}>
          Bearbeiten
        </Button>
        <Button size="sm" variant="danger-outline" iconName="trash" onClick={() => onDelete(index)}>
          Löschen
        </Button>
      </div>
    ),
  };
}

function zoneSpec(
  onEdit: (entry: FirewallZone, index: number) => void,
  onDelete: (index: number) => void,
): CollectionSpec<FirewallZone> {
  return {
    key: "zones",
    title: "Zonen",
    singular: "Zone",
    description: "Welche Netze zusammengehören und was sie dürfen",
    blank: {
      id: "",
      name: "",
      input: "REJECT",
      output: "ACCEPT",
      forward: "REJECT",
      masq: false,
      network: [],
    },
    fields: [
      { key: "name", label: "Name", kind: "text" },
      { key: "network", label: "Netze", kind: "list", help: "kommagetrennt, z.B. lan, debug" },
      { key: "input", label: "Input", kind: "select", options: POLICIES },
      { key: "output", label: "Output", kind: "select", options: POLICIES },
      { key: "forward", label: "Forward", kind: "select", options: POLICIES },
      { key: "masq", label: "Masquerading (NAT)", kind: "bool" },
    ],
    columns: [
      { key: "name", header: "Zone", cell: (z) => <span className="font-medium">{z.name}</span> },
      {
        key: "net",
        header: "Netze",
        mono: true,
        hideOnMobile: true,
        cell: (z) => z.network?.join(", ") || "—",
      },
      { key: "in", header: "Input", cell: (z) => targetBadge(z.input) },
      { key: "out", header: "Output", cell: (z) => targetBadge(z.output) },
      { key: "fwd", header: "Forward", cell: (z) => targetBadge(z.forward) },
      {
        key: "masq",
        header: "NAT",
        hideOnMobile: true,
        cell: (z) => (z.masq ? "ja" : "nein"),
      },
      actionColumn(onEdit, onDelete),
    ],
    empty: "Keine Zonen definiert.",
  };
}

function forwardingSpec(
  choices: FirewallChoices,
  onEdit: (entry: FirewallForwarding, index: number) => void,
  onDelete: (index: number) => void,
): CollectionSpec<FirewallForwarding> {
  return {
    key: "forwardings",
    title: "Weiterleitungen zwischen Zonen",
    singular: "Weiterleitung",
    description: "Aus welcher Zone in welche geroutet werden darf",
    blank: { id: "", src: "lan", dest: "wan" },
    fields: [
      { key: "src", label: "von Zone", kind: "select", options: choices.zones },
      { key: "dest", label: "nach Zone", kind: "select", options: choices.zones },
    ],
    columns: [
      { key: "src", header: "von", cell: (f) => f.src },
      { key: "dest", header: "nach", cell: (f) => f.dest },
      { key: "id", header: "UCI", mono: true, hideOnMobile: true, cell: (f) => f.id || "neu" },
      actionColumn(onEdit, onDelete),
    ],
    empty: "Keine Weiterleitungen.",
  };
}

function redirectSpec(
  choices: FirewallChoices,
  onEdit: (entry: FirewallRedirect, index: number) => void,
  onDelete: (index: number) => void,
): CollectionSpec<FirewallRedirect> {
  return {
    key: "redirects",
    title: "Port-Freigaben",
    singular: "Port-Freigabe",
    description: "Von außen auf einen Port → an ein Gerät im LAN (DNAT)",
    blank: {
      id: "",
      name: "",
      src: "wan",
      srcIp: "",
      srcDport: "",
      dest: "lan",
      destIp: "",
      destPort: "",
      proto: "tcp",
      target: "DNAT",
      family: "ipv4",
      enabled: true,
    },
    fields: [
      { key: "name", label: "Name", kind: "text" },
      { key: "src", label: "von Zone", kind: "select", options: choices.zones, help: "meist wan" },
      { key: "srcDport", label: "Port außen", kind: "text", help: "z.B. 8080 oder 8000-8010" },
      {
        key: "dest",
        label: "Ziel-Zone",
        kind: "select",
        options: choices.zones,
        help: "meist lan",
      },
      {
        key: "destIp",
        label: "Ziel-IP",
        kind: "combo",
        options: choices.clients,
        emptyLabel: "— Gerät wählen —",
        help: choices.clientsLoading
          ? "Geräte werden vom Router geholt …"
          : "Gerät aus ARP/DHCP oder eigene Adresse",
      },
      { key: "destPort", label: "Ziel-Port", kind: "text" },
      { key: "proto", label: "Protokoll", kind: "select", options: PROTOCOLS },
      { key: "srcIp", label: "nur von IP", kind: "text", help: "leer = von überall" },
      { key: "family", label: "Familie", kind: "select", options: FAMILIES },
      { key: "enabled", label: "Aktiv", kind: "bool" },
    ],
    columns: [
      { key: "enabled", header: "Aktiv", cell: (r) => activeBadge(r.enabled) },
      { key: "name", header: "Name", cell: (r) => r.name || "—" },
      {
        key: "from",
        header: "von außen",
        cell: (r) => [r.src ?? "*", r.srcDport].filter(Boolean).join(" : "),
      },
      {
        key: "to",
        header: "nach innen",
        mono: true,
        cell: (r) => [r.destIp, r.destPort].filter(Boolean).join(" : ") || "—",
      },
      { key: "proto", header: "Proto", hideOnMobile: true, cell: (r) => r.proto ?? "alle" },
      actionColumn(onEdit, onDelete),
    ],
    empty: "Keine Port-Freigaben.",
  };
}

function ruleSpec(
  choices: FirewallChoices,
  onEdit: (entry: FirewallRule, index: number) => void,
  onDelete: (index: number) => void,
): CollectionSpec<FirewallRule> {
  return {
    key: "rules",
    title: "Regeln",
    singular: "Regel",
    description: "Was durchgelassen oder abgewiesen wird",
    blank: {
      id: "",
      name: "",
      src: "wan",
      dest: null,
      proto: "tcp",
      srcIp: null,
      destIp: null,
      srcPort: null,
      destPort: null,
      target: "ACCEPT",
      family: "any",
      enabled: true,
    },
    fields: [
      { key: "name", label: "Name", kind: "text" },
      {
        key: "src",
        label: "Quell-Zone",
        kind: "select",
        options: choices.zones,
        emptyLabel: "der Router selbst",
      },
      {
        key: "dest",
        label: "Ziel-Zone",
        kind: "select",
        options: choices.zones,
        emptyLabel: "der Router selbst",
      },
      { key: "proto", label: "Protokoll", kind: "select", options: PROTOCOLS, emptyLabel: "alle" },
      { key: "srcIp", label: "Quell-IP", kind: "text" },
      { key: "srcPort", label: "Quell-Port", kind: "text" },
      { key: "destIp", label: "Ziel-IP", kind: "text" },
      { key: "destPort", label: "Ziel-Port", kind: "text" },
      { key: "target", label: "Aktion", kind: "select", options: POLICIES },
      { key: "family", label: "Familie", kind: "select", options: FAMILIES },
      { key: "enabled", label: "Aktiv", kind: "bool" },
    ],
    columns: [
      { key: "enabled", header: "Aktiv", cell: (r) => activeBadge(r.enabled) },
      { key: "name", header: "Name", cell: (r) => r.name || "—" },
      {
        key: "src",
        header: "Quelle",
        cell: (r) => [r.src ?? "*", r.srcIp, r.srcPort].filter(Boolean).join(" · "),
      },
      {
        key: "dest",
        header: "Ziel",
        cell: (r) => [r.dest ?? "Gerät", r.destIp, r.destPort].filter(Boolean).join(" · "),
      },
      { key: "proto", header: "Proto", hideOnMobile: true, cell: (r) => r.proto ?? "alle" },
      { key: "target", header: "Aktion", cell: (r) => targetBadge(r.target) },
      actionColumn(onEdit, onDelete),
    ],
    empty: "Keine Regeln definiert.",
  };
}

export function firewallSpecs(
  choices: FirewallChoices,
  onEdit: (collection: CollectionKey, entry: FirewallEntry, index: number) => void,
  onDelete: (collection: CollectionKey, index: number) => void,
): CollectionSpec<FirewallEntry>[] {
  const bind =
    <T extends FirewallEntry>(key: CollectionKey) =>
    (entry: T, index: number) =>
      onEdit(key, entry, index);
  const cut = (key: CollectionKey) => (index: number) => onDelete(key, index);
  return [
    redirectSpec(choices, bind("redirects"), cut("redirects")),
    ruleSpec(choices, bind("rules"), cut("rules")),
    zoneSpec(bind("zones"), cut("zones")),
    forwardingSpec(choices, bind("forwardings"), cut("forwardings")),
  ] as CollectionSpec<FirewallEntry>[];
}

/** Die vier Listen aus dem Snapshot — fehlende zählen als leer. */
export function collections(state: FirewallState | null): FirewallState {
  return {
    zones: state?.zones ?? [],
    forwardings: state?.forwardings ?? [],
    redirects: state?.redirects ?? [],
    rules: state?.rules ?? [],
  };
}
